import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Popover } from '@mui/material'
import {
    composePhoneNumber,
    contractDefaultPhoneCountryIso,
    DEFAULT_PHONE_COUNTRY_ISO,
    formatNationalPhone,
    flagEmoji,
    getPhoneCountry,
    looksLikeInternationalPhone,
    nationalDigitLimit,
    parsePastedPhone,
    parsePhoneInput,
    PINNED_PHONE_COUNTRY_ISOS,
    searchPhoneCountries,
    type PhoneCountry,
} from '../methods/phoneCountries'
import './PhoneNumberInput.sass'

export { contractDefaultPhoneCountryIso }

type PhoneCountrySelectProps = {
    valueIso: string
    onChange: (iso: string) => void
    disabled?: boolean
    variant?: 'compact' | 'field'
    id?: string
}

export function PhoneCountrySelect({
    valueIso,
    onChange,
    disabled = false,
    variant = 'compact',
    id,
}: PhoneCountrySelectProps) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const anchorRef = useRef<HTMLButtonElement>(null)
    const searchRef = useRef<HTMLInputElement>(null)
    const country = getPhoneCountry(valueIso)
    const options = useMemo(() => searchPhoneCountries(query), [query])

    useEffect(() => {
        if (!open) {
            return
        }
        const timer = window.setTimeout(() => searchRef.current?.focus(), 0)
        return () => window.clearTimeout(timer)
    }, [open])

    function close() {
        setOpen(false)
        setQuery('')
    }

    function selectCountry(iso: string) {
        onChange(iso)
        close()
    }

    const showPinnedDivider = !query.trim() && options.length > PINNED_PHONE_COUNTRY_ISOS.length

    return (
        <>
            <button
                ref={anchorRef}
                type="button"
                id={id}
                className={
                    variant === 'field'
                        ? 'phone-country-select phone-country-select--field'
                        : 'phone-number-input__country'
                }
                data-testid={variant === 'compact' ? 'phone-country-button' : 'phone-country-field'}
                aria-label="Код страны"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        setOpen(true)
                    }
                }}
            >
                <span className="phone-country-select__flag" aria-hidden>{flagEmoji(country.iso)}</span>
                {variant === 'field' ? (
                    <span className="phone-country-select__label">
                        {country.nameRu} (+{country.callingCode})
                    </span>
                ) : (
                    <span className="phone-country-select__code">+{country.callingCode}</span>
                )}
                <span className="phone-country-select__chevron" aria-hidden>▾</span>
            </button>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        className: 'phone-country-dropdown',
                        style: { width: Math.max(anchorRef.current?.offsetWidth ?? 0, 280) },
                    },
                }}
            >
                <div className="phone-country-dropdown__search">
                    <input
                        ref={searchRef}
                        data-testid="phone-country-search"
                        className="phone-country-dropdown__search-input"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Страна или код"
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                close()
                            }
                            if (event.key === 'Enter' && options[0]) {
                                event.preventDefault()
                                selectCountry(options[0].iso)
                            }
                        }}
                    />
                </div>
                <div className="phone-country-dropdown__list" role="listbox">
                    {options.length === 0 && (
                        <div className="phone-country-dropdown__empty">Ничего не найдено</div>
                    )}
                    {options.map((option, index) => (
                        <React.Fragment key={option.iso}>
                            {showPinnedDivider && index === PINNED_PHONE_COUNTRY_ISOS.length && (
                                <div className="phone-country-dropdown__divider" />
                            )}
                            <CountryOption
                                country={option}
                                selected={option.iso === country.iso}
                                onSelect={selectCountry}
                            />
                        </React.Fragment>
                    ))}
                </div>
            </Popover>
        </>
    )
}

function CountryOption({
    country,
    selected,
    onSelect,
}: {
    country: PhoneCountry
    selected: boolean
    onSelect: (iso: string) => void
}) {
    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            data-testid={`phone-country-option-${country.iso}`}
            className={`phone-country-dropdown__option${selected ? ' is-selected' : ''}`}
            onClick={() => onSelect(country.iso)}
        >
            <span className="phone-country-select__flag" aria-hidden>{flagEmoji(country.iso)}</span>
            <span className="phone-country-dropdown__name">{country.nameRu}</span>
            <span className="phone-country-dropdown__code">+{country.callingCode}</span>
        </button>
    )
}

type PhoneNumberInputProps = {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
    id?: string
    className?: string
    defaultCountryIso?: string
    autoComplete?: string
}

export default function PhoneNumberInput({
    value,
    onChange,
    placeholder = 'Номер телефона',
    disabled = false,
    id,
    className = '',
    defaultCountryIso = DEFAULT_PHONE_COUNTRY_ISO,
    autoComplete = 'tel',
}: PhoneNumberInputProps) {
    const fallbackIso = getPhoneCountry(defaultCountryIso).iso
    const parsedInitial = parsePhoneInput(value || '', fallbackIso)
    const [selectedIso, setSelectedIso] = useState(parsedInitial.iso)
    const [national, setNational] = useState(parsedInitial.nationalDigits)
    const userPickedIso = useRef(false)
    const lastEmitted = useRef(value || '')

    useEffect(() => {
        if (value || userPickedIso.current) {
            return
        }
        setSelectedIso(fallbackIso)
    }, [fallbackIso, value])

    useEffect(() => {
        if ((value || '') === lastEmitted.current) {
            return
        }
        lastEmitted.current = value || ''
        const parsed = parsePhoneInput(value || '', userPickedIso.current ? selectedIso : fallbackIso)
        setSelectedIso(parsed.iso)
        setNational(parsed.nationalDigits)
        // incoming value from parent/autofill; selectedIso is only a parse fallback
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, fallbackIso])

    function emit(iso: string, nationalDigits: string) {
        const next = composePhoneNumber(iso, nationalDigits)
        lastEmitted.current = next
        onChange(next)
    }

    function applyRawInput(raw: string, fromPaste = false) {
        if (fromPaste || looksLikeInternationalPhone(raw, selectedIso)) {
            const parsed = fromPaste
                ? parsePastedPhone(raw, selectedIso)
                : parsePhoneInput(raw, selectedIso)
            userPickedIso.current = true
            setSelectedIso(parsed.iso)
            setNational(parsed.nationalDigits)
            emit(parsed.iso, parsed.nationalDigits)
            return
        }
        const digits = raw.replace(/\D/g, '')
        if (digits.length > nationalDigitLimit(selectedIso)) {
            return
        }
        setNational(digits)
        emit(selectedIso, digits)
    }

    function handleCountryChange(iso: string) {
        userPickedIso.current = true
        setSelectedIso(iso)
        emit(iso, national)
    }

    const composed = composePhoneNumber(selectedIso, national)

    return (
        <div
            className={`phone-number-input ui-kit-v2 ${className}${disabled ? ' is-disabled' : ''}`.trim()}
            data-testid="phone-number-input"
            data-composed={composed}
        >
            <PhoneCountrySelect
                valueIso={selectedIso}
                onChange={handleCountryChange}
                disabled={disabled}
                variant="compact"
            />
            <span className="phone-number-input__divider" aria-hidden />
            <input
                id={id}
                data-testid="phone-national-input"
                className="phone-number-input__national"
                type="tel"
                inputMode="tel"
                name={autoComplete === 'tel' ? 'phone' : 'sign-phone'}
                autoComplete={autoComplete}
                placeholder={placeholder}
                disabled={disabled}
                value={formatNationalPhone(national, selectedIso)}
                onChange={(event) => applyRawInput(event.target.value)}
                onInput={(event) => applyRawInput((event.target as HTMLInputElement).value)}
                onPaste={(event) => {
                    const text = event.clipboardData.getData('text')
                    if (!text) {
                        return
                    }
                    event.preventDefault()
                    applyRawInput(text, true)
                }}
            />
        </div>
    )
}
